import { z } from 'zod'
import { FieldError, FieldErrors, get } from 'react-hook-form'
import { useTranslations } from 'next-intl'

type Errors = Record<PropertyKey, FieldError | undefined>

export type ErrorMap<T> = {
  [F in keyof T]: {
    [C in z.core.$ZodIssueCode]?: string
  }
}

interface GetValidationErrorMessagesParams<T> {
  field: keyof FieldErrors
  nestedPath?: string
  errorMap: ErrorMap<T>
  errors: FieldErrors
}

export const useFormValidationMessages = () => {
  const t = useTranslations('common.forms.validation')
  const fieldLabel = t('field_label_fallback')

  const mustBeNCharsLong = (field: string, n: number) =>
    t('field_must_be_exactly_n_chars', { field, n })

  const cannotBeMoreThanNCharsLong = (field: string, n: number) =>
    t('field_cannot_be_more_than_n_chars', { field, n })

  const mustBeAtLeastNCharsLong = (field: string, n: number) =>
    t('field_must_be_at_least_n_chars', { field, n })

  const mustBeSelected = (field: string) => t('field_must_be_selected', { field })

  const cannotBeEmpty = (field?: string) =>
    t('field_cannot_be_empty', { field: field ?? fieldLabel })

  const notAValid = (field: string) => t('field_not_valid', { field })

  const cannotBeSelectedMultipleTimes = (field: string) => t('field_only_selected_once', { field })

  const unknownValidationError = t('unknown_error')

  const getValidationErrorMessage = <T extends Errors>({
    field,
    errorMap,
    nestedPath,
    errors,
  }: GetValidationErrorMessagesParams<T>) => {
    const fieldError = get(errors, field)
    const nestedError = get(fieldError, nestedPath)
    const error = nestedError ?? fieldError

    if (!error) {
      return undefined
    }

    const errorMsg = errorMap[field]?.[error.type as z.core.$ZodIssueCode]

    return errorMsg ?? unknownValidationError
  }

  return {
    mustBeNCharsLong,
    mustBeSelected,
    cannotBeMoreThanNCharsLong,
    cannotBeEmpty,
    notAValid,
    cannotBeSelectedMultipleTimes,
    mustBeAtLeastNCharsLong,
    unknownValidationError,
    getValidationErrorMessage,
  }
}
